import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Sayfa başlığındaki aksiyon şeridine **alt bileşenlerden** buton eklemek için portal slotu.
 *
 * <p><b>Neden gerekli (kullanıcı isteği 2026-08-26):</b> Figma tablo tasarımı (180561:29596) "Yenile"
 * ve "Excel'e Aktar"ı, "Oluştur" butonunun <b>solunda</b>, sayfa başlığı satırında gösteriyor. Oysa o
 * butonlar tabloyu çizen <b>panelin</b> içinde yaşıyor ve `PageHeader` panelin <b>üstünde</b>, kardeş
 * konumda duruyor.
 *
 * <p><b>Neden durum yukarı taşınMADI:</b> "Excel'e Aktar"ın sorgusu panelin filtre durumunun
 * <b>tamamına</b> bağlıdır (kapsam, arama, statü, önem, alt-aksiyon türü, gecikme, sıralama + pano
 * süzgeçleri). Bu durumu sayfaya taşımak gerçek bir gerileme riski taşır ve kodda kayıtlı bir olayın
 * tekrarı olurdu: 2026-08-21'de süzgeçlerden biri düştüğü için ekranda 3 satır görünürken indirilen
 * dosyaya tüm zamanların kayıtları inmişti. Portal, <b>yalnız çizim yerini</b> taşır — durum ve sorgu
 * paneldeki yerinde kalır.
 *
 * <p><b>Yerleşim:</b> {@code PageHeader} aksiyon şeridinin içinde İKİ kap çizer — önce bu slot, sonra
 * kendi {@code actions} içeriği. Portal DOM'a eklendiği için slot içeriği doğal olarak
 * {@code actions}'ın <b>soluna</b> düşer; tasarımdaki sıra ([Yenile] [Excel'e Aktar] [+ Oluştur]) ek
 * bir kural gerektirmeden çıkar.
 *
 * <p><b>Sayfa başlığı olmayan ekranlar:</b> slot {@code null} kalır ve {@link PageHeaderActionsSlot}
 * çocuklarını <b>bulunduğu yerde</b> çizer. Bu bilinçli bir yedektir — aksi hâlde başlıksız bir
 * ekranda butonlar sessizce <b>kaybolurdu</b>.
 *
 * <p><b>Sınır:</b> bir sayfada birden çok tablo aynı slota yazarsa butonlar üst üste birikir ve hangi
 * tabloya ait oldukları belirsizleşir. **Çözüm ayrı bir şerit DEĞİL** (eski `.ui-table-toolbar`
 * kalıbı 2026-08-27'de tamamen kalktı, sınıf da silindi): sekmeli ekranlarda aksiyonlar **aktif
 * sekmeye göre** çizilir, böylece aynı anda yalnız görünen tablonun butonu bulunur (bkz. Şablon
 * Parametreleri). Sekmesiz ama gerçekten iki tablolu bir ekran çıkarsa, ayrım o ekranda kurulmalıdır.
 */
interface Ctx {
  el: HTMLElement | null
  setEl: (el: HTMLElement | null) => void
}

const PageHeaderActionsContext = createContext<Ctx | null>(null)

export function PageHeaderActionsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [el, setEl] = useState<HTMLElement | null>(null)
  const value = useMemo(() => ({ el, setEl }), [el])
  return (
    <PageHeaderActionsContext.Provider value={value}>{children}</PageHeaderActionsContext.Provider>
  )
}

/**
 * {@code PageHeader} kullanır: slot kabını context'e bildirir.
 *
 * <p>Callback ref döndürür — `useEffect` + `useRef` ikilisi yerine bilinçli tercih: kap DOM'a
 * girdiği/çıktığı anda context güncellenir, araya bir render turu girmez. Unmount'ta `null` gelir ve
 * slot temizlenir (yoksa panel sökülmüş bir düğüme portal çizmeye çalışırdı).
 */
export function usePageHeaderActionsRef(): (el: HTMLElement | null) => void {
  const setEl = useContext(PageHeaderActionsContext)?.setEl
  return (el) => setEl?.(el)
}

/**
 * Paneller kullanır: çocuklarını sayfa başlığındaki slota çizer; slot yoksa bulunduğu yerde çizer
 * (yedek — gerekçe sınıf javadoc'unda).
 */
export function PageHeaderActionsSlot({ children }: Readonly<{ children: ReactNode }>) {
  const el = useContext(PageHeaderActionsContext)?.el ?? null
  if (el == null) return <>{children}</>
  return createPortal(children, el)
}
